'use client';

import {
  ContactPlacement,
  GA_MEASUREMENT_ID,
  PLACEMENT_ATTRIBUTE,
  isAnalyticsHost,
  trackGaEvent,
} from '@/lib/analytics';
import Script from 'next/script';
import { useEffect, useState } from 'react';

/**
 * 전화·카카오톡 상담 링크 클릭. 링크가 여러 화면(헤더·플로팅·지점 상세)에 흩어져 있어
 * 문서 전체 클릭을 한곳에서 본다.
 *
 * 같은 대표번호가 여러 화면에 걸려 있어 링크 주소만으로는 어디서 눌렸는지 알 수 없다.
 * data-ga-placement 를 거슬러 올라가 위치를 함께 싣는다(없으면 other).
 *
 * 링크가 아니라 버튼 onClick 에서 window.open('tel:') 하는 곳(side-cta)은 여기로 안 들어오므로
 * 그쪽에서 직접 trackGaEvent 를 부른다.
 */
function trackContactClick(event: MouseEvent): void {
  if (!(event.target instanceof Element)) return;
  const link = event.target.closest('a[href]');
  if (!link) return;

  const href = link.getAttribute('href') ?? '';
  const placement =
    link
      .closest(`[${PLACEMENT_ATTRIBUTE}]`)
      ?.getAttribute(PLACEMENT_ATTRIBUTE) ?? ContactPlacement.OTHER;

  if (href.startsWith('tel:')) {
    trackGaEvent('click_call', { link_url: href, placement });
  } else if (href.includes('pf.kakao.com')) {
    trackGaEvent('click_kakao_chat', { link_url: href, placement });
  }
}

/**
 * Google 애널리틱스(GA4) — 방문 시작 페이지·유입 경로·페이지 이동·상담 행동.
 *
 * - gtag.js 는 layout 의 구글 광고 태그(AW-)가 이미 불러오므로 다시 불러오지 않고 config 만 더한다
 *   (구글 태그 하나에 대상 여러 개를 거는 공식 방식).
 * - 운영 도메인에서만 켠다. 서버는 도메인을 모르므로 마운트 후에만 렌더한다(하이드레이션 불일치 방지).
 * - SPA 페이지뷰는 직접 보내지 않는다. GA4 향상된 측정의 "브라우저 기록 이벤트 기반 페이지 변경"이
 *   자동으로 잡으므로 여기서 또 보내면 이동 한 번에 두 번 집계된다.
 * - 상담 신청 완료(generate_lead)는 상담 폼이 trackGaEvent 로 보낸다.
 */
export default function GoogleAnalytics() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!isAnalyticsHost()) return;
    setEnabled(true);
    document.addEventListener('click', trackContactClick, true);
    return () => document.removeEventListener('click', trackContactClick, true);
  }, []);

  if (!enabled) return null;

  return (
    <Script id="ga4-config" strategy="afterInteractive">
      {`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('config', '${GA_MEASUREMENT_ID}');`}
    </Script>
  );
}
